import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Typography,
} from "@material-ui/core";
import {
  ContactSupport,
  ErrorOutline,
  ExpandMore,
  Refresh,
} from "@material-ui/icons";
import PropTypes from "prop-types";
import React from "react";
import { languages } from "../constants/languages";

/**
 * Catches render errors in child UI so surrounding layout (header, nav) stays visible.
 * Pass resetKey (e.g. location.pathname) so navigation clears the error state.
 */
export default class PageErrorBoundary extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    resetKey: PropTypes.string,
  };

  state = {
    hasError: false,
    error: null,
    detailsOpen: false,
  };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("PageErrorBoundary:", error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.resetKey || "";
    const next = this.props.resetKey || "";
    if (prev !== next && this.state.hasError) {
      this.setState({ hasError: false, error: null, detailsOpen: false });
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const err = this.state.error;
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
          width="100%"
          minHeight={200}
        >
          <Paper
            elevation={2}
            style={{
              maxWidth: 650,
              width: "100%",
              padding: 24,
              textAlign: "center",
            }}
          >
            <ErrorOutline color="error" style={{ fontSize: 48, marginBottom: 8 }} />
            <Typography variant="h6" component="h2" gutterBottom>
              {languages.general.pageRenderErrorTitle}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {languages.general.pageRenderErrorDescription}
            </Typography>
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              style={{ gap: 8, marginTop: 16 }}
            >
              <Button
                variant="contained"
                color="primary"
                component="a"
                href="https://tournifyapp.com/contact"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<ContactSupport />}
              >
                {languages.general.pageRenderErrorContactSupport}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
              >
                {languages.general.refreshPage}
              </Button>
            </Box>
            <Box width="100%" marginTop={2} textAlign="left">
              <Accordion
                expanded={this.state.detailsOpen}
                onChange={() =>
                  this.setState((s) => ({ detailsOpen: !s.detailsOpen }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2" color="textSecondary">
                    {languages.general.pageRenderErrorTechnicalDetails}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper
                    variant="outlined"
                    style={{
                      width: "100%",
                      padding: 12,
                      fontFamily: "monospace",
                      fontSize: 12,
                      overflow: "auto",
                    }}
                  >
                    <Typography variant="body2" component="pre" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                      {err.message}
                      {err.stack ? `\n\n${err.stack}` : ""}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}
